// Presentation-layer transform for the canonical, verifier-produced personalization-point marker.
// It recognizes ONLY the exact canonical format the factual-integrity contract emits:
//
//   PERSONALIZATION POINT PP-001 (effort_18.case_study_example): <requirement text>
//
// and replaces it with a clearly differentiated, human-readable fill-in prompt. It NEVER fabricates
// the missing fact and performs NO broad sanitization (ordinary prose is untouched). The backend
// factual contract, verifier logic, and generation prompts are unaffected — this is display only.

export const PP_PROMPT_SENTINEL = "\u270D\uFE0F"; // ✍️

// slug -> friendly imperative. Anything unmapped falls back to the generic prompt below.
const IMPERATIVES = {
  case_study_example: "Add your real example",
  data_point: "Add your data point",
  data_source: "Add your source",
  origin_story: "Add your origin detail",
  current_activity: "Add what you're working on now",
  core_belief: "Add your core belief",
  searchable_positioning: "Add your searchable positioning",
  signature_belief: "Add your signature belief",
  common_question: "Add the question you're asked most",
  actual_process: "Add your real process",
  transformation_result: "Add your real transformation",
  week_one_reflection: "Add your early-cycle reflection",
  real_questions: "Add your real audience questions",
  honest_opinion: "Add your honest opinion",
  actual_method: "Add your real method",
  recommended_resources: "Add your recommended resources",
  offering_detail: "Add your offer detail",
  authoritative_take: "Add your authoritative take",
  correction_stance: "Add the advice you'd correct",
  hard_won_lesson: "Add your hard-won lesson",
  behind_the_scenes_detail: "Add a behind-the-scenes detail",
  consistency_result: "Add your real result",
  author_identity: "Add your name",
};
const GENERIC = "Add your detail";

// Canonical marker: PP id + canonical key (effort_NN.slug) + trailing requirement (to line end).
const PP_MARKER_SRC =
  "PERSONALIZATION POINT\\s+(PP-\\d{3,})\\s*\\((effort_\\d{1,2}\\.[a-z0-9_]+)\\)\\s*:[ \\t]*([^\\n\\r]*)";

// C-series legacy safety net: a NARROW set of malformed variants that older/compact generations
// (e.g. Pinterest) may have already saved to the Vault, strictly anchored on a valid PP-\d{3,} id
// immediately paired with a valid (effort_NN.slug) canonical key. This recognizes:
//   * a missing "PERSONALIZATION POINT" prefix:  PP-001 (effort_16.data_point): ...
//   * a missing prefix AND missing ": requirement":  PP-001 (effort_16.data_point)
// The backend verifier now GUARANTEES the canonical form going forward; this only protects existing
// content. It stays anchored on the id-immediately-followed-by-(key) pair so it can never consume
// ordinary prose, and requirement capture stops at the line end.
const PP_LEGACY_SRC =
  "(?:PERSONALIZATION POINT\\s+)?(PP-\\d{3,})\\s*\\((effort_\\d{1,2}\\.[a-z0-9_]+)\\)(?:\\s*:[ \\t]*([^\\n\\r]*))?";

function slugOf(key) {
  const dot = key.indexOf(".");
  return dot >= 0 ? key.slice(dot + 1) : key;
}

function promptFor(key, requirement) {
  const imp = IMPERATIVES[slugOf(key)] || GENERIC;
  const req = (requirement || "").trim().replace(/\s+/g, " ").replace(/\s+([.,;:])/g, "$1");
  return req ? `${PP_PROMPT_SENTINEL} ${imp} — ${req}` : `${PP_PROMPT_SENTINEL} ${imp}`;
}

// Replace every recognized PP marker (canonical form, plus the narrow legacy variants) with its
// human-readable prompt. Ordinary prose is untouched. Idempotent: an already-transformed string
// contains no id-immediately-followed-by-(key) pair, so re-running (e.g. on legacy Vault text)
// leaves it unchanged.
export function toCustomerText(text) {
  if (text == null) return text;
  return String(text).replace(new RegExp(PP_LEGACY_SRC, "gi"), (_m, _id, key, req) =>
    promptFor(key, req));
}

// Internal metadata: the PP ids + canonical keys present in the raw text (preserved, never shown).
export function extractPoints(text) {
  const out = [];
  if (text == null) return out;
  const re = new RegExp(PP_LEGACY_SRC, "gi");
  let m;
  while ((m = re.exec(String(text))) !== null) {
    out.push({ id: m[1], key: m[2], requirement: (m[3] || "").trim() });
  }
  return out;
}

// Count of unresolved fill-in prompts in an already-transformed, customer-facing string — used for
// the non-blocking "needs your input" indicator. Counts the differentiated sentinel.
export function countPrompts(customerText) {
  if (customerText == null) return 0;
  return (String(customerText).match(/\u270D\uFE0F/g) || []).length;
}
